export const MAX_THUMBNAILS_PER_CLIP = 8;
export const THUMBNAIL_CACHE_LIMIT = 96;
export const THUMBNAIL_DECODER_LIMIT = 2;
export const THUMBNAIL_WIDTH = 160;
export const THUMBNAIL_HEIGHT = 90;
export const THUMBNAIL_TIMEOUT_MS = 8000;

export function thumbnailCount(pixelWidth) {
  return Math.max(1, Math.min(MAX_THUMBNAILS_PER_CLIP, Math.floor((Number.isFinite(pixelWidth) ? pixelWidth : 0) / 90)));
}

export function thumbnailSampleTimes(clip, pixelWidth) {
  const start = clip?.source_start ?? 0;
  const duration = clip?.duration;
  const speed = clip?.speed ?? 1;
  if (![start, duration, speed].every(Number.isFinite) || start < 0 || duration <= 0 || speed <= 0) return [];
  const count = thumbnailCount(pixelWidth);
  return Array.from({ length: count }, (_, index) => start + (index + .5) / count * duration * speed);
}

export function createThumbnailFrameCache(limit = THUMBNAIL_CACHE_LIMIT) {
  const maximum = Number.isFinite(limit) ? Math.max(1, Math.min(THUMBNAIL_CACHE_LIMIT, Math.floor(limit))) : THUMBNAIL_CACHE_LIMIT;
  const frames = new Map();
  return {
    get size() { return frames.size; },
    get(key) {
      if (!frames.has(key)) return undefined;
      const frame = frames.get(key);
      frames.delete(key); frames.set(key, frame);
      return frame;
    },
    set(key, frame) {
      frames.delete(key); frames.set(key, frame);
      while (frames.size > maximum) frames.delete(frames.keys().next().value);
    },
    clear() { frames.clear(); },
  };
}

const cache = createThumbnailFrameCache();
const queued = [];
let active = 0;
const dom = { createVideo: () => document.createElement('video'), createCanvas: () => document.createElement('canvas') };
const frameKey = (url, time) => JSON.stringify([url, time, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT]);

function failure(name, message) {
  const error = new Error(message);
  error.name = name;
  return error;
}

const aborted = () => failure('AbortError', 'Thumbnail extraction cancelled');
const decodeFailed = () => failure('ThumbnailDecodeError', 'Thumbnail frame unavailable');
const timedOut = () => failure('TimeoutError', 'Thumbnail extraction timed out');
function checkAbort(signal) { if (signal?.aborted) throw aborted(); }

function drain() {
  while (active < THUMBNAIL_DECODER_LIMIT && queued.length) {
    const job = queued.shift();
    job.signal?.removeEventListener('abort', job.cancel);
    if (job.signal?.aborted) { job.reject(aborted()); continue; }
    active += 1;
    Promise.resolve().then(job.run).then(job.resolve, job.reject).finally(() => { active -= 1; drain(); });
  }
}

function enqueue(run, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(aborted()); return; }
    const job = { run, signal, resolve, reject };
    job.cancel = () => {
      const index = queued.indexOf(job);
      if (index !== -1) { queued.splice(index, 1); reject(aborted()); }
    };
    signal?.addEventListener('abort', job.cancel, { once: true });
    queued.push(job); drain();
  });
}

function waitForFrame(video, events, ready, action, signal, deadline) {
  return new Promise((resolve, reject) => {
    let timer;
    const finish = error => {
      clearTimeout(timer);
      for (const event of events) video.removeEventListener(event, available);
      video.removeEventListener('error', failed);
      signal?.removeEventListener('abort', cancel);
      if (error) reject(error); else resolve();
    };
    const available = () => { if (ready()) finish(); };
    const failed = () => finish(decodeFailed());
    const cancel = () => finish(aborted());
    if (signal?.aborted) { reject(aborted()); return; }
    const remaining = deadline - Date.now();
    if (remaining <= 0) { reject(timedOut()); return; }
    for (const event of events) video.addEventListener(event, available);
    video.addEventListener('error', failed);
    signal?.addEventListener('abort', cancel, { once: true });
    timer = setTimeout(() => finish(timedOut()), remaining);
    try { action(); available(); } catch { finish(decodeFailed()); }
  });
}

function releaseVideo(video) {
  if (!video) return;
  try { video.pause(); } catch { /* A partially initialized decoder still needs its source removed. */ }
  try { video.removeAttribute('src'); } catch { /* Cleanup must not prevent the queue releasing its slot. */ }
  try { video.load(); } catch { /* A removed source can reject during browser teardown. */ }
  video.remove?.();
}

async function decodeFrames(url, times, signal, factory, timeoutMs) {
  checkAbort(signal);
  const keys = times.map(time => frameKey(url, time));
  const existing = keys.map(key => cache.get(key));
  if (existing.every(Boolean)) return existing;
  const deadline = Date.now() + timeoutMs;
  let video, canvas;
  try {
    video = factory.createVideo();
    video.crossOrigin = 'anonymous'; video.preload = 'auto'; video.muted = true; video.playsInline = true;
    await waitForFrame(video, ['loadedmetadata'], () => video.readyState >= 1 && video.videoWidth > 0 && video.videoHeight > 0,
      () => { video.src = url; video.load(); }, signal, deadline);
    canvas = factory.createCanvas();
    canvas.width = THUMBNAIL_WIDTH; canvas.height = THUMBNAIL_HEIGHT;
    const context = canvas.getContext('2d');
    if (!context) throw decodeFailed();
    const ratio = Math.min(THUMBNAIL_WIDTH / video.videoWidth, THUMBNAIL_HEIGHT / video.videoHeight);
    const width = video.videoWidth * ratio, height = video.videoHeight * ratio;
    const frames = [];
    for (let index = 0; index < times.length; index += 1) {
      checkAbort(signal);
      const cached = cache.get(keys[index]);
      if (cached) { frames.push(cached); continue; }
      const time = times[index];
      if (Number.isFinite(video.duration) && time >= video.duration) throw decodeFailed();
      await waitForFrame(video, ['seeked', 'loadeddata'],
        () => !video.seeking && video.readyState >= 2 && Math.abs(video.currentTime - time) < .0001,
        () => { video.currentTime = time; }, signal, deadline);
      checkAbort(signal);
      context.fillStyle = '#000000';
      context.fillRect(0, 0, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT);
      context.drawImage(video, (THUMBNAIL_WIDTH - width) / 2, (THUMBNAIL_HEIGHT - height) / 2, width, height);
      const src = canvas.toDataURL('image/jpeg', .72);
      if (!src.startsWith('data:image/jpeg')) throw decodeFailed();
      const frame = { time, src, width: THUMBNAIL_WIDTH, height: THUMBNAIL_HEIGHT };
      cache.set(keys[index], frame); frames.push(frame);
    }
    return frames;
  } catch (error) {
    if (['AbortError', 'TimeoutError', 'ThumbnailDecodeError'].includes(error.name)) throw error;
    throw decodeFailed();
  } finally {
    releaseVideo(video);
    if (canvas) { canvas.width = 0; canvas.height = 0; }
  }
}

export function renderSampleFrames({ url, clip, pixelWidth, signal, domFactory = dom, timeoutMs = THUMBNAIL_TIMEOUT_MS }) {
  if (signal?.aborted) return Promise.reject(aborted());
  const times = thumbnailSampleTimes(clip, pixelWidth);
  if (typeof url !== 'string' || !url || !times.length) return Promise.resolve([]);
  const ready = times.map(time => cache.get(frameKey(url, time)));
  if (ready.every(Boolean)) return Promise.resolve(ready);
  const timeout = Number.isFinite(timeoutMs) ? Math.max(1, Math.min(THUMBNAIL_TIMEOUT_MS, timeoutMs)) : THUMBNAIL_TIMEOUT_MS;
  return enqueue(() => decodeFrames(url, times, signal, domFactory, timeout), signal);
}

export function clearThumbnailCache() { cache.clear(); }
export function thumbnailRuntimeStats() { return { activeDecoders: active, queuedJobs: queued.length, cachedFrames: cache.size }; }
